import { useEffect, useState } from 'react';
import SafeIcon from '../common/SafeIcon';
import supabase from '../supabase/supabase';
import { requestEmail } from '../services/emailNotifications';
import { COUNTRY_DIAL_CODES } from '../data/countryDialCodes';
import './contact-widgets.css';

const TABLE = 'site_enquiries_1700000000002';
const initialForm = {
  guest_name: '',
  email: '',
  country_code: '',
  phone: '',
  room_slug: '',
  arrival_date: '',
  departure_date: '',
  guest_count: '2',
  message: ''
};

function InquiryWidget({ roomSlug = '', openRequest = false, onOpenHandled }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ ...initialForm, room_slug: roomSlug.toLowerCase() });
  const [countrySearch, setCountrySearch] = useState('');
  const [status, setStatus] = useState('');
  const [saving, setSaving] = useState(false);
  const set = (key) => (event) => setForm((current) => ({ ...current, [key]: event.target.value }));
  const matchingCountries = COUNTRY_DIAL_CODES.filter(([country, code]) =>
    `${country} +${code}`.toLowerCase().includes(countrySearch.trim().toLowerCase())
  );

  useEffect(() => {
    if (openRequest) {
      setOpen(true);
      onOpenHandled?.();
    }
  }, [openRequest, onOpenHandled]);

  useEffect(() => {
    const openEnquiry = () => setOpen(true);
    window.addEventListener('open-enquiry', openEnquiry);
    return () => window.removeEventListener('open-enquiry', openEnquiry);
  }, []);

  const submit = async (event) => {
    event.preventDefault();
    if (!form.guest_name.trim() || !form.email.trim() || !form.country_code || !form.phone.trim() ||
      !form.room_slug || !form.arrival_date || !form.departure_date || !form.message.trim() ||
      !Number.isInteger(Number(form.guest_count)) || Number(form.guest_count) < 1) {
      setStatus('Please complete every field before sending your enquiry.');
      return;
    }
    if (form.arrival_date && form.departure_date && form.departure_date <= form.arrival_date) {
      setStatus('Departure must be after arrival.');
      return;
    }
    setSaving(true);
    setStatus('');
    const payload = {
      ...form,
      guest_name: form.guest_name.trim(),
      email: form.email.trim(),
      phone: `+${form.country_code} ${form.phone.trim()}`,
      message: form.message.trim(),
      arrival_date: form.arrival_date || null,
      departure_date: form.departure_date || null,
      guest_count: Number(form.guest_count) || 1
    };
    delete payload.country_code;
    const { error } = await supabase.from(TABLE).insert(payload);
    if (error) {
      setStatus(`We could not save your enquiry: ${error.message}`);
      setSaving(false);
      return;
    }
    const mail = await requestEmail({ type: 'enquiry', enquiry: payload });
    setStatus(mail.ok
      ? 'Thank you. Your enquiry has been saved and the email service accepted the notification request.'
      : 'Thank you. Your enquiry has been saved. Email notification could not be confirmed.');
    setForm({ ...initialForm, room_slug: roomSlug.toLowerCase() });
    setCountrySearch('');
    setSaving(false);
  };

  return (
    <>
      <div className="contact-floats">
        <a className="contact-floats__whatsapp" href="https://wa.me/94743630000" target="_blank" rel="noreferrer" aria-label="Chat with us on WhatsApp">
          <SafeIcon name="MessageCircle" /><span>WhatsApp</span>
        </a>
        <button className="contact-floats__enquire" type="button" onClick={() => setOpen(true)}>
          <SafeIcon name="Mail" /><span>Make an enquiry</span>
        </button>
      </div>
      {open && (
        <div className="inquiry-backdrop" role="presentation" onMouseDown={(event) => event.target === event.currentTarget && setOpen(false)}>
          <section className="inquiry-panel" role="dialog" aria-modal="true" aria-labelledby="inquiry-title">
            <button className="inquiry-panel__close" type="button" onClick={() => setOpen(false)} aria-label="Close enquiry panel"><SafeIcon name="X" /></button>
            <span className="inquiry-panel__eyebrow">PLAN YOUR STAY</span>
            <h2 id="inquiry-title">Send us an enquiry</h2>
            <p>Tell us a little about your visit and our team will be in touch.</p>
            <form className="inquiry-form" onSubmit={submit}>
              <label>Your name<input required autoComplete="name" value={form.guest_name} onChange={set('guest_name')} /></label>
              <label>Email address<input required type="email" autoComplete="email" value={form.email} onChange={set('email')} /></label>
              <label>Search country
                <input type="search" value={countrySearch} onChange={(event) => setCountrySearch(event.target.value)} placeholder="Search country or dial code" autoComplete="off" />
              </label>
              <label>Country code
                <select required value={form.country_code} onChange={set('country_code')} autoComplete="tel-country-code">
                  <option value="">Select country code</option>
                  {form.country_code && !matchingCountries.some(([, code]) => code === form.country_code) && (
                    <option value={form.country_code}>Selected (+{form.country_code})</option>
                  )}
                  {matchingCountries.map(([country, code]) => <option key={country} value={code}>{country} (+{code})</option>)}
                </select>
              </label>
              <label>Phone or WhatsApp<input required type="tel" autoComplete="tel" value={form.phone} onChange={set('phone')} /></label>
              <label>Room<select required value={form.room_slug} onChange={set('room_slug')}><option value="" disabled>Any room</option><option value="kotiya">Kotiya</option><option value="walaha">Walaha</option><option value="aliya">Aliya</option></select></label>
              <div className="inquiry-form__dates">
                <label>Arrival<input required type="date" value={form.arrival_date} onChange={set('arrival_date')} /></label>
                <label>Departure<input required type="date" value={form.departure_date} onChange={set('departure_date')} /></label>
              </div>
              <label>Guests<input required type="number" min="1" max="20" value={form.guest_count} onChange={set('guest_count')} /></label>
              <label>Message<textarea required rows="3" value={form.message} onChange={set('message')} placeholder="How can we help with your stay?" /></label>
              <button className="inquiry-form__submit" type="submit" disabled={saving}>{saving ? 'Sending…' : 'Send enquiry'} <SafeIcon name="ArrowRight" /></button>
              {status && <p className="inquiry-form__status" role="status">{status}</p>}
            </form>
          </section>
        </div>
      )}
    </>
  );
}

export default InquiryWidget;